import { useEffect, useMemo, useState } from 'react';
import { InitiativeBenchmarking } from '@g17eco/types/initiative';
import { Loader } from '@g17eco/atoms/loader';
import { BenchmarkingTemplate } from '../BenchmarkingTemplate';
import { LatestSurveyFilters } from './latest-survey-filters';
import { hasCompletedSurveys } from './utils';
import './styles.scss';
import { SURVEY } from '@constants/terminology';
import { useGetBenchmarkingSurveysQuery } from '@api/portfolio-tracker';
import { ExchangeSurveyWithCompany } from './types';
import { LatestSurveyTable } from './latest-survey-table';
import { type SurveyFilters } from '@g17eco/types/portfolio-benchmarking';
import { saveBenchmarkingFilters, loadBenchmarkingFilters } from '@utils/portfolioFilterStorage';
import { useBenchmarkingDateRange } from '../BenchmarkingDateRangeContext';

interface ApplyFilterCompanies {
  companies: InitiativeBenchmarking[];
  filters: Pick<SurveyFilters, 'sector' | 'industry'>;
}

const applyFilterCompanies = ({ companies, filters }: ApplyFilterCompanies): InitiativeBenchmarking[] => {
  const sectorCondition = (company: InitiativeBenchmarking) =>
    filters.sector ? company.sectorText === filters.sector : true;

  const industryCondition = (company: InitiativeBenchmarking) =>
    filters.industry ? company.industryText === filters.industry : true;

  return companies.filter((company) => sectorCondition(company) && industryCondition(company));
};

export const LatestSurveyProgress = ({ portfolioId }: { portfolioId: string }) => {
  const { dateRange, timeRange, setDateTimeRange } = useBenchmarkingDateRange();

  const [filters, setFilters] = useState<Omit<SurveyFilters, 'dateRange' | 'timeRange'>>(() => {
    const saved = loadBenchmarkingFilters(portfolioId);
    return {
      dateField: saved?.dateField || 'effectiveDate',
      completedSurveysOnly: saved?.completedSurveysOnly || false,
      latestSurveysOnly: saved?.latestSurveysOnly || false,
      sector: saved?.sector,
      industry: saved?.industry,
      surveyPacks: saved?.surveyPacks,
    };
  });

  const { latestSurveysOnly, completedSurveysOnly, dateField, surveyPacks } = filters;

  const { data: benchmarkingCompanies, isLoading: loading } = useGetBenchmarkingSurveysQuery({
    portfolioId,
    filters: { dateRange, surveyPacks, dateField },
  });

  const filteredCompanies = useMemo(
    () =>
      applyFilterCompanies({
        companies: benchmarkingCompanies ?? [],
        filters: { sector: filters.sector, industry: filters.industry },
      }),
    [benchmarkingCompanies, filters.sector, filters.industry],
  );

  const filteredSurveys = useMemo(() => {
    return filteredCompanies.reduce((acc, { latestSurvey, benchmarkingSurveys, ...company }) => {
      if (!latestSurvey || !benchmarkingSurveys) {
        return acc;
      }

      (latestSurveysOnly ? [latestSurvey] : benchmarkingSurveys).forEach((survey) => {
        if (completedSurveysOnly && !survey.completedDate) {
          return;
        }

        acc.push({ ...survey, company });
      });

      return acc;
    }, [] as ExchangeSurveyWithCompany[]);
  }, [completedSurveysOnly, filteredCompanies, latestSurveysOnly]);

  const companiesWithCompletedSurvey = useMemo(
    () => filteredCompanies.filter(hasCompletedSurveys),
    [filteredCompanies],
  );

  const companiesWithCompletedSurveyPercent = filteredCompanies.length
    ? Number((companiesWithCompletedSurvey.length / filteredCompanies.length) * 100)
    : 0;

  const completedSurveyPercentage = filteredSurveys.length
    ? Number((filteredSurveys.filter((survey) => survey.completedDate).length / filteredSurveys.length) * 100)
    : 0;

  const completedReportsCount = useMemo(
    () => filteredSurveys.filter((survey) => survey.completedDate).length,
    [filteredSurveys]
  );

  // Save filters to sessionStorage whenever they change (dateRange/timeRange handled by context)
  useEffect(() => {
    saveBenchmarkingFilters(portfolioId, filters);
  }, [portfolioId, filters]);

  const handleChangeFilters = (newFilters: SurveyFilters) => {
    // Extract dateRange/timeRange to update context
    const { dateRange: newDateRange, timeRange: newTimeRange, ...otherFilters } = newFilters;

    // Update context if dateRange or timeRange changed
    if (newTimeRange !== undefined && (newDateRange !== dateRange || newTimeRange !== timeRange)) {
      setDateTimeRange(newDateRange, newTimeRange);
    }

    // Update local filters (without dateRange/timeRange)
    setFilters(otherFilters);
  };

  return (
    <BenchmarkingTemplate>
      <div className='latest-survey-progress'>
        <LatestSurveyFilters
          filteredSurveys={filteredSurveys}
          filters={{ ...filters, dateRange, timeRange }}
          onChangeFilters={handleChangeFilters}
        />
        <LatestSurveyTable
          portfolioId={portfolioId}
          filteredSurveys={filteredSurveys}
          hasNoCompany={!benchmarkingCompanies?.length}
          pageSize={50}
        />
        {loading && <Loader />}
        <div className='mt-3 companies-completed-survey'>
          <span className='text-HeadingLight text-uppercase'>NUMBER OF COMPLETED {SURVEY.PLURAL}</span>
          <span className='companies-completed-survey__percent'>
            {completedReportsCount} | {completedSurveyPercentage > 0 ? completedSurveyPercentage.toFixed(1) : 0}%
          </span>
        </div>
        <div className='mt-3 companies-completed-survey'>
          <span className='text-HeadingLight text-uppercase'>COMPANIES WITH COMPLETED {SURVEY.SINGULAR}</span>
          <span className='companies-completed-survey__percent'>
            {companiesWithCompletedSurvey.length} | {companiesWithCompletedSurveyPercent > 0 ? companiesWithCompletedSurveyPercent.toFixed(1) : 0}%
          </span>
        </div>
      </div>
    </BenchmarkingTemplate>
  );
};
